import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Pagination,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CreatorCrossService from "../service/CreatorCrossService";
import StatsByStateTable from "../../admin/pzl_common/stats/StatsByStateTable";
import Logger from "../../services/Logger";
import { getContentLanguage, getOrderedContentLanguages, setContentLanguage } from "../../services/LangPrefs";
import GeoService from "../../services/GeoService";
import PuzzleCard from "../common/PuzzleCard";
import PuzzleStateFilter from "../common/PuzzleStateFilter";
import PuzzleExcludeStateFilter from "../common/PuzzleExcludeStateFilter";
import { trackEvent } from "../../services/GoogleAnalytics";

export default function CreatorCrosswordMain() {
    const navigate = useNavigate();
    const [contentLang, setContentLangState] = useState(getContentLanguage());
    const [isQuebec, setIsQuebec] = useState(false);
    const [puzzles, setPuzzles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [tagCounts, setTagCounts] = useState([]);
    const [tagCountsLoading, setTagCountsLoading] = useState(true);

    useEffect(() => {
        trackEvent("entered", { name: "CreatorCrosswordMain" });
    }, []);
    const [selectedTags, setSelectedTags] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize] = useState(15);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const STATE_FILTER_STORAGE_KEY = "creator_crossword_state_filter";

    const [selectedState, setSelectedState] = useState(() => {
        try {
            return localStorage.getItem(STATE_FILTER_STORAGE_KEY) || "";
        } catch {
            return "";
        }
    });
    const [excludeDiscarded, setExcludeDiscarded] = useState(true);
    const [excludeDisabled, setExcludeDisabled] = useState(true);

    useEffect(() => {
        const checkQuebec = async () => {
            try {
                const quebecStatus = await GeoService.isQuebec();
                setIsQuebec(quebecStatus);
            } catch (error) {
                Logger.error("Error checking Quebec status:", error);
                setIsQuebec(false);
            }
        };
        checkQuebec();
    }, []);

    useEffect(() => {
        const loadStats = async () => {
            setStatsLoading(true);
            try {
                const statsData = await CreatorCrossService.getCrossStats();
                setStats(statsData);
            } catch (error) {
                Logger.error("Error loading stats:", error);
            } finally {
                setStatsLoading(false);
            }
        };
        loadStats();
    }, [contentLang]);

    useEffect(() => {
        const loadTagCounts = async () => {
            setTagCountsLoading(true);
            try {
                const tags = await CreatorCrossService.getCrossTags(contentLang, "READY_TO_REVIEW,REVIEWED,PUBLISHED");
                const list = Array.isArray(tags) ? tags : [];
                const bedrockLower = "bedrock";
                const items = list
                    .map((item) => {
                        const name = typeof item === "string" ? item : (item?.tag ?? item?.name ?? String(item ?? ""));
                        const count = typeof item === "object" && item != null && typeof item.count === "number" ? item.count : 0;
                        return { tag: name, count };
                    })
                    .filter((x) => x.tag && x.count > 0 && !String(x.tag).toLowerCase().includes(bedrockLower))
                    .sort((a, b) => a.count - b.count);
                setTagCounts(items);
            } catch (error) {
                Logger.error("Error loading tag counts:", error);
                setTagCounts([]);
            } finally {
                setTagCountsLoading(false);
            }
        };
        loadTagCounts();
    }, [contentLang]);

    useEffect(() => {
        loadPuzzles();
    }, [page, contentLang, selectedState, selectedTags, excludeDiscarded, excludeDisabled]);

    const loadPuzzles = async () => {
        setLoading(true);
        try {
            const state = selectedState || null;
            const tags = selectedTags.length > 0 ? selectedTags : null;
            const result = await CreatorCrossService.listCrosswordPuzzles(page, pageSize, 'createdAt:desc', null, state, null, contentLang, excludeDiscarded, excludeDisabled, tags);
            if (result) {
                setPuzzles(result.content || []);
                setTotalPages(result.page?.totalPages ?? 0);
                setTotalElements(result.page?.totalElements ?? 0);
            }
        } catch (error) {
            Logger.error("Error loading puzzles:", error);
            setPuzzles([]);
        } finally {
            setLoading(false);
        }
    };

    const handleContentLanguageChange = (_event, newLanguage) => {
        if (newLanguage !== null) {
            setContentLangState(newLanguage);
            setContentLanguage(newLanguage);
            setPage(0); // Reset to first page when language changes
        }
    };

    const handleTagClick = (tag) => {
        setSelectedTags((prev) => (prev.includes(tag) ? [] : [tag]));
        setPage(0);
    };

    const handleStateChange = (event) => {
        const value = event.target.value;
        setSelectedState(value);
        setPage(0);
        try {
            localStorage.setItem(STATE_FILTER_STORAGE_KEY, value || "");
        } catch (_) {}
    };

    const handlePageChange = (_event, value) => {
        setPage(value - 1); // Pagination is 1-based, but API is 0-based
    };

    const handlePuzzleClick = (puzzleId) => {
        navigate(`/creator/crossword/edit/${puzzleId}`);
    };

    const handleGetRandomDraft = async () => {
        try {
            // Navigate to crossword edit page, which will fetch the draft and redirect
            navigate("/creator/crossword/edit");
        } catch (error) {
            Logger.error("Error getting random draft:", error);
        }
    };

    const englishStats = stats?.byLanguage?.english
        ? { crossByState: stats.byLanguage.english.crossByState }
        : null;
    const frenchStats = stats?.byLanguage?.french
        ? { crossByState: stats.byLanguage.french.crossByState }
        : null;

    return (
        <Box sx={{ p: { xs: 1.5, sm: 4 } }}>
            {/* Top Bar - Language Buttons (Left) and Action Buttons (Right) */}
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                mb: 2,
                gap: { xs: 2, sm: 0 }
            }}>
                {/* Language Buttons - Left */}
                <Box>
                    <ToggleButtonGroup
                        value={contentLang}
                        exclusive
                        onChange={handleContentLanguageChange}
                        size="small"
                    >
                        {[...getOrderedContentLanguages(isQuebec)].sort((a, b) => (a.code === 'english' ? -1 : 1)).map((lang) => (
                            <ToggleButton
                                key={lang.code}
                                value={lang.code}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        },
                                    },
                                }}
                            >
                                {lang.code === 'french' ? 'French' : 'English'}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                {/* Action Buttons - Right */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    gap: 1,
                    width: { xs: '100%', sm: 'auto' }
                }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<RocketLaunchIcon />}
                        onClick={() => navigate('/creator/guide')}
                        size="small"
                        sx={{ 
                            px: 2,
                            py: 1,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        Quick Start
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleGetRandomDraft}
                        size="small"
                        sx={{ 
                            px: 2,
                            py: 1,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        New Crossword
                    </Button>
                </Box>
            </Box>

            {/* Stats Section - Crossword puzzle by state */}
            {statsLoading && (
                <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading statistics...</Typography>
                </Box>
            )}
            {!statsLoading && (englishStats || frenchStats) && (
                <Box sx={{ mb: 3 }}>
                    <StatsByStateTable
                        englishStats={englishStats}
                        frenchStats={frenchStats}
                        title="Crossword puzzle by state"
                        contentLanguage={contentLang}
                        hideCrossword={false}
                        hideSumRow
                    />
                </Box>
            )}

            {/* Count by tags */}
            <Box sx={{ mb: 3 }}>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                            Count by tags
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Number of puzzles by tag that are ready to serve users (Ready to Review, Reviewed, and Published).
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                color: "success.dark",
                                fontStyle: "italic",
                                fontWeight: "bold"
                            }}
                        >
                            Select less-count tags and create puzzles to make them BALANCE. All tags should have the same count.
                        </Typography>
                        {tagCountsLoading ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">Loading tags...</Typography>
                            </Box>
                        ) : tagCounts.length > 0 ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                {tagCounts.map(({ tag, count }) => {
                                    const isSelected = selectedTags.includes(tag);
                                    return (
                                        <Chip
                                            key={tag}
                                            label={`${tag} ${count}`}
                                            size="small"
                                            variant={isSelected ? "filled" : "outlined"}
                                            color={isSelected ? "primary" : "default"}
                                            onClick={() => handleTagClick(tag)}
                                            sx={{ fontSize: "0.8rem", cursor: "pointer" }}
                                        />
                                    );
                                })}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No tags with counts</Typography>
                        )}
                    </CardContent>
                </Card>
            </Box>

            {/* State Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <PuzzleStateFilter
                    value={selectedState}
                    onChange={handleStateChange}
                />
                <Box sx={{ flex: 1 }} />
                {!selectedState && (
                    <PuzzleExcludeStateFilter
                        excludeDiscarded={excludeDiscarded}
                        excludeDisabled={excludeDisabled}
                        onExcludeDiscardedChange={(e) => setExcludeDiscarded(e.target.checked)}
                        onExcludeDisabledChange={(e) => setExcludeDisabled(e.target.checked)}
                    />
                )}
            </Box>

            {/* Puzzles List */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : puzzles.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No crossword puzzles found. Click "New Crossword" to create a puzzle.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {puzzles.map((puzzle) => (
                            <Grid key={puzzle.id} size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                                <PuzzleCard
                                    puzzle={puzzle}
                                    onClick={() => handlePuzzleClick(puzzle.id)}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page + 1}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                        Showing {puzzles.length} of {totalElements} puzzles
                    </Typography>
                </>
            )}
        </Box>
    );
}

